import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, Plus, Upload, ChevronLeft, ChevronRight, Users, AlertCircle, AlertTriangle, Download, Star, StickyNote, Flame } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner, EmptyState } from "@/components/ui/page";
import { StageBadge } from "@/components/StageBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtCurrency, fmtDate, daysSince, LEAD_SOURCES } from "@/lib/format";

const STAGES = ["ALL", "LEAD", "PROSPECT", "RE_ENGAGED", "DEAL", "WON", "LOST"];

export default function Contacts() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { selected, isAll, currentClient } = useClient();
  const { isReadOnly } = useAuth();
  const qc = useQueryClient();

  const stage = sp.get("stage") || "ALL";
  const stale = sp.get("stale") === "true";
  const priorityFilter = sp.get("priority") === "true";
  const warmFilter = sp.get("warm") === "true";
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [source, setSource] = useState("ALL");
  const [page, setPage] = useState(1);

  useEffect(() => { const t = setTimeout(() => setDebounced(search), 300); return () => clearTimeout(t); }, [search]);
  useEffect(() => { setPage(1); }, [stage, debounced, source, stale, priorityFilter, warmFilter]);

  const single = !isAll && selected;

  const { data: counts } = useQuery({
    queryKey: ["contact-counts", selected],
    queryFn: async () => (await api.get(`/clients/${selected}/contacts/counts`)).data,
    enabled: !!single,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["contacts", selected, stage, debounced, source, stale, priorityFilter, warmFilter, page],
    queryFn: async () => (await api.get(`/clients/${selected}/contacts`, {
      params: { stage, search: debounced || undefined, source: source === "ALL" ? undefined : source, stale: stale ? "true" : undefined, priority: priorityFilter ? "true" : undefined, warm: warmFilter ? "true" : undefined, page, page_size: 25 },
    })).data,
    enabled: !!single,
  });

  const setStage = (s) => { const n = new URLSearchParams(sp); n.set("stage", s); setSp(n); };
  const toggleStale = () => { const n = new URLSearchParams(sp); stale ? n.delete("stale") : n.set("stale", "true"); setSp(n); };
  const togglePriority = () => { const n = new URLSearchParams(sp); priorityFilter ? n.delete("priority") : n.set("priority", "true"); setSp(n); };
  const toggleWarm = () => { const n = new URLSearchParams(sp); warmFilter ? n.delete("warm") : n.set("warm", "true"); setSp(n); };
  const priorityMut = useMutation({
    mutationFn: async ({ id, is_priority }) => (await api.post(`/clients/${selected}/contacts/${id}/priority`, { is_priority })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["contacts"] }); qc.invalidateQueries({ queryKey: ["contact-counts"] }); qc.invalidateQueries({ queryKey: ["priority-projects"] }); },
  });
  const exportXlsx = async () => {
    const res = await api.get(`/clients/${selected}/contacts-export-xlsx`, {
      params: { stage: stage === "ALL" ? undefined : stage, source: source === "ALL" ? undefined : source, search: debounced || undefined },
      responseType: "blob",
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = `contacts-${stage.toLowerCase()}.xlsx`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportCsv = async () => {
    const res = await api.get(`/clients/${selected}/export`, {
      params: {
        dataset: "contacts",
        stage: stage !== "ALL" ? stage : undefined,
        search: debounced || undefined,
        source: source === "ALL" ? undefined : source,
        stale: stale ? "true" : undefined,
      },
      responseType: "blob",
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `contacts-${stale ? "needs-attention" : stage.toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isAll) return (
    <div><PageHeader title="Contacts" />
      <EmptyState icon={AlertCircle} title="Select a client" subtitle="Choose a specific client from the sidebar to view its CRM contacts." /></div>
  );

  const items = data?.items || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / 25));

  return (
    <div>
      <PageHeader title="Contacts" subtitle={currentClient?.business_name}>
        {!isReadOnly && (
          <Button variant="outline" onClick={() => navigate("/import")} data-testid="import-contacts-link">
            <Upload className="h-4 w-4 mr-1.5" /> Import
          </Button>
        )}
        {!isReadOnly && <AddContactDialog clientId={selected} onDone={() => { qc.invalidateQueries({ queryKey: ["contacts"] }); qc.invalidateQueries({ queryKey: ["contact-counts"] }); }} />}
      </PageHeader>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200 mb-4 overflow-x-auto">
        {STAGES.map((s) => (
          <button key={s} data-testid={`contacts-tab-${s}`} onClick={() => setStage(s)}
            className={`px-4 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${stage === s ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {s === "ALL" ? "All" : s === "RE_ENGAGED" ? "Re-Engaged" : s.charAt(0) + s.slice(1).toLowerCase()}
            {counts && <span className="ml-1.5 text-xs text-slate-400">{counts[s] ?? 0}</span>}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="contacts-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, phone, email, address, salesperson…" className="pl-9" />
        </div>
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="w-[190px]" data-testid="contacts-source-filter"><SelectValue placeholder="Source" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Sources</SelectItem>
            {LEAD_SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <button onClick={toggleStale} data-testid="stale-filter-toggle"
          className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border transition-colors ${stale ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
          <AlertTriangle className="h-4 w-4" /> Needs Attention{counts?.STALE ? ` (${counts.STALE})` : ""}
        </button>
        <button onClick={togglePriority} data-testid="priority-filter-toggle"
          className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border transition-colors ${priorityFilter ? "bg-amber-400 text-white border-amber-400" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
          <Star className={`h-4 w-4 ${priorityFilter ? "fill-white" : ""}`} /> Priority{counts?.PRIORITY ? ` (${counts.PRIORITY})` : ""}
        </button>
        <button onClick={toggleWarm} data-testid="warm-filter-toggle"
          className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border transition-colors ${warmFilter ? "bg-orange-500 text-white border-orange-500" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
          <Flame className={`h-4 w-4 ${warmFilter ? "fill-white" : ""}`} /> Warm{counts?.WARM ? ` (${counts.WARM})` : ""}
        </button>
        <Button variant="outline" onClick={exportCsv} data-testid="export-contacts-btn">
          <Download className="h-4 w-4 mr-1.5" /> Export{typeof total === "number" ? ` (${total})` : ""}
        </Button>
        <Button variant="outline" onClick={exportXlsx} data-testid="export-contacts-xlsx-btn">
          <Download className="h-4 w-4 mr-1.5" /> Excel + Notes
        </Button>
      </div>

      {isLoading ? <Spinner /> : items.length === 0 ? (
        <EmptyState icon={Users} title="No contacts found" subtitle="Adjust filters or import your lead list." />
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="hidden lg:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Contact</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Salesperson</TableHead>
                  <TableHead>Last Activity</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((c) => {
                  const lastTouch = c.last_activity_at || c.original_lead_date;
                  const ds = daysSince(lastTouch);
                  const isStale = ["LEAD", "PROSPECT"].includes(c.current_stage) && ds != null && ds > 14;
                  return (
                    <TableRow key={c.id} data-testid={`contact-row-${c.id}`} className="cursor-pointer hover:bg-slate-50"
                      onClick={() => navigate(`/contacts/${c.id}`)}>
                      <TableCell>
                        <div className="font-semibold text-slate-900 flex items-center gap-1.5">
                          <button onClick={(e) => { e.stopPropagation(); priorityMut.mutate({ id: c.id, is_priority: !c.is_priority }); }} data-testid={`star-toggle-${c.id}`} className="shrink-0">
                            <Star className={`h-4 w-4 ${c.is_priority ? "fill-amber-400 text-amber-400" : "text-slate-300 hover:text-amber-400"}`} />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); navigate(`/contacts/${c.id}`); }} data-testid={`contact-name-${c.id}`} className="text-left hover:underline text-blue-700">
                            {c.first_name} {c.last_name}
                          </button>
                          {c.is_warm && <span data-testid={`warm-badge-${c.id}`} className="inline-flex items-center gap-0.5 text-[10px] font-bold text-orange-700 bg-orange-100 border border-orange-200 rounded px-1.5 py-0.5"><Flame className="h-3 w-3" /> WARM</span>}
                          {isStale && <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" data-testid={`stale-flag-${c.id}`} />}
                        </div>
                        <div className="text-xs text-slate-500">{c.mobile_phone || c.email || "—"}</div>
                        <ContactNotes contact={c} />
                      </TableCell>
                      <TableCell><StageBadge stage={c.current_stage} /></TableCell>
                      <TableCell className="text-sm text-slate-600">{c.lead_source || "—"}</TableCell>
                      <TableCell className="text-sm text-slate-600">{c.salesperson || "—"}</TableCell>
                      <TableCell className="text-sm">
                        {c.last_activity_at ? (
                          <span className={ds > 14 ? "text-rose-600 font-medium" : "text-slate-600"}>{fmtDate(c.last_activity_at)} · {ds}d</span>
                        ) : (
                          <span className={isStale ? "text-rose-600 font-medium" : "text-slate-400"}>No activity{isStale && ds != null ? ` · ${ds}d` : ""}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium tabular-nums text-slate-700">
                        {c.actual_deal_value ? fmtCurrency(c.actual_deal_value) : c.estimated_value ? <span className="text-slate-400">{fmtCurrency(c.estimated_value)}</span> : "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Mobile cards */}
          <div className="lg:hidden divide-y divide-slate-100">
            {items.map((c) => {
              const lastTouch = c.last_activity_at || c.original_lead_date;
              const ds = daysSince(lastTouch);
              const isStale = ["LEAD", "PROSPECT"].includes(c.current_stage) && ds != null && ds > 14;
              const val = c.actual_deal_value ? fmtCurrency(c.actual_deal_value) : c.estimated_value ? fmtCurrency(c.estimated_value) : null;
              return (
                <div key={c.id} data-testid={`contact-card-${c.id}`} onClick={() => navigate(`/contacts/${c.id}`)}
                  className="w-full text-left p-4 active:bg-slate-50 cursor-pointer">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex items-start gap-2">
                      <button onClick={(e) => { e.stopPropagation(); priorityMut.mutate({ id: c.id, is_priority: !c.is_priority }); }} data-testid={`star-toggle-${c.id}`} className="shrink-0 mt-0.5">
                        <Star className={`h-4 w-4 ${c.is_priority ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                      </button>
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 flex items-center gap-1.5">
                          <button onClick={(e) => { e.stopPropagation(); navigate(`/contacts/${c.id}`); }} data-testid={`contact-name-${c.id}`} className="truncate text-left hover:underline text-blue-700">{c.first_name} {c.last_name}</button>
                          {c.is_warm && <span data-testid={`warm-badge-${c.id}`} className="inline-flex items-center gap-0.5 text-[10px] font-bold text-orange-700 bg-orange-100 border border-orange-200 rounded px-1.5 py-0.5 shrink-0"><Flame className="h-3 w-3" /> WARM</span>}
                          {isStale && <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" data-testid={`stale-flag-${c.id}`} />}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 truncate">{c.mobile_phone || c.email || "—"}</div>
                        <ContactNotes contact={c} />
                      </div>
                    </div>
                    <StageBadge stage={c.current_stage} />
                  </div>
                  <div className="mt-3 flex items-center justify-between text-xs">
                    <span className="text-slate-500">
                      {c.last_activity_at
                        ? <span className={ds > 14 ? "text-rose-600 font-medium" : ""}>{fmtDate(c.last_activity_at)} · {ds}d</span>
                        : <span className={isStale ? "text-rose-600 font-medium" : "text-slate-400"}>No activity{isStale && ds != null ? ` · ${ds}d` : ""}</span>}
                    </span>
                    {val && <span className="font-semibold tabular-nums text-slate-700">{val}</span>}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-sm">
            <span className="text-slate-500">{total} contacts · page {page} of {totalPages}</span>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((x) => x - 1)} data-testid="page-prev"><ChevronLeft className="h-4 w-4" /></Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((x) => x + 1)} data-testid="page-next"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ContactNotes({ contact }) {
  const notes = contact?.notes_list || [];
  if (!notes.length) return null;
  return (
    <div className="mt-1.5 space-y-1" data-testid={`contact-notes-${contact.id}`}>
      {notes.map((n, i) => (
        <div key={i} className="flex items-start gap-1.5 text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded px-2 py-1">
          <StickyNote className="h-3 w-3 text-slate-400 mt-0.5 shrink-0" />
          <span>
            {n.created_at && <span className="text-slate-400 mr-1">{String(n.created_at).slice(0, 10)}:</span>}
            {n.text}
          </span>
        </div>
      ))}
    </div>
  );
}

function AddContactDialog({ clientId, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", mobile_phone: "", current_stage: "LEAD", lead_source: "Website", salesperson: "", estimated_value: "", notes: "" });
  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const mut = useMutation({
    mutationFn: async () => (await api.post(`/clients/${clientId}/contacts`, {
      ...f, estimated_value: f.estimated_value ? Number(f.estimated_value) : null,
    })).data,
    onSuccess: () => { toast.success("Contact added"); setOpen(false); setF({ first_name: "", last_name: "", email: "", mobile_phone: "", current_stage: "LEAD", lead_source: "Website", salesperson: "", estimated_value: "", notes: "" }); onDone(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="add-contact-btn"><Plus className="h-4 w-4 mr-1.5" /> Add Contact</Button></DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>New Contact</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label>First Name *</Label><Input data-testid="new-first-name" value={f.first_name} onChange={(e) => upd("first_name", e.target.value)} className="mt-1" /></div>
          <div><Label>Last Name</Label><Input data-testid="new-last-name" value={f.last_name} onChange={(e) => upd("last_name", e.target.value)} className="mt-1" /></div>
          <div><Label>Email</Label><Input data-testid="new-email" value={f.email} onChange={(e) => upd("email", e.target.value)} className="mt-1" /></div>
          <div><Label>Phone</Label><Input data-testid="new-phone" value={f.mobile_phone} onChange={(e) => upd("mobile_phone", e.target.value)} className="mt-1" /></div>
          <div>
            <Label>Stage</Label>
            <Select value={f.current_stage} onValueChange={(v) => upd("current_stage", v)}>
              <SelectTrigger className="mt-1" data-testid="new-stage"><SelectValue /></SelectTrigger>
              <SelectContent>{["LEAD", "PROSPECT", "RE_ENGAGED", "DEAL", "WON", "LOST"].map((s) => <SelectItem key={s} value={s}>{s.replace("_", "-")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Source</Label>
            <Select value={f.lead_source} onValueChange={(v) => upd("lead_source", v)}>
              <SelectTrigger className="mt-1" data-testid="new-source"><SelectValue /></SelectTrigger>
              <SelectContent>{LEAD_SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Salesperson</Label><Input value={f.salesperson} onChange={(e) => upd("salesperson", e.target.value)} className="mt-1" /></div>
          <div><Label>Estimated Value</Label><Input data-testid="new-est-value" type="number" value={f.estimated_value} onChange={(e) => upd("estimated_value", e.target.value)} className="mt-1" /></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea value={f.notes} onChange={(e) => upd("notes", e.target.value)} className="mt-1" rows={2} /></div>
        </div>
        <DialogFooter>
          <Button disabled={!f.first_name || mut.isPending} onClick={() => mut.mutate()} data-testid="save-contact-btn">Save Contact</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
